/**
 * 通用 API 客户端工具
 * 基于 fetch，支持跨域请求、统一的错误处理和重试机制
 */

import { API_CONFIG } from '../config/api.config';

// API 响应类型定义
export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message?: string;
  code?: number;
}

// 请求配置类型
export interface RequestConfig extends RequestInit {
  timeout?: number;
  baseURL?: string;
  params?: Record<string, unknown>;
  retry?: {
    enabled?: boolean;
    maxRetries?: number;
    retryDelay?: number;
  };
  onSuccess?: (data: unknown) => void; // 成功回调
  onError?: (error: Error) => void; // 失败回调
}

// 默认配置
const DEFAULT_CONFIG: RequestConfig = {
  timeout: API_CONFIG.TIMEOUT,
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  mode: 'cors', // 支持跨域
  credentials: 'omit', // 不携带 cookies，避免跨域问题
  retry: {
    enabled: API_CONFIG.RETRY.enabled,
    maxRetries: API_CONFIG.RETRY.maxRetries,
    retryDelay: API_CONFIG.RETRY.retryDelay,
  },
};

/**
 * 创建带超时的 fetch 请求
 */
function fetchWithTimeout(url: string, config: RequestConfig): Promise<Response> {
  const { timeout = DEFAULT_CONFIG.timeout, ...fetchConfig } = config;
  
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`请求超时: ${timeout}ms`));
    }, timeout);

    fetch(url, fetchConfig)
      .then(response => {
        clearTimeout(timer);
        resolve(response);
      })
      .catch(error => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

/**
 * 构建查询参数
 */
function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

/**
 * 延迟函数
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * 判断错误是否可重试
 */
function isRetryableError(error: Error): boolean {
  const retryableErrors = [
    'Failed to fetch',
    'Network request failed',
    'NetworkError',
    'timeout',
    '请求超时',
  ];
  return retryableErrors.some(msg => error.message.includes(msg));
}

/**
 * 通用 API 请求方法（带重试机制）
 * @param url 请求地址
 * @param config 请求配置
 * @returns Promise<T> 直接返回接口数据
 */
export async function apiRequest<T = unknown>(
  url: string, 
  config: RequestConfig = {}
): Promise<T> {
  const retryConfig = {
    ...DEFAULT_CONFIG.retry,
    ...config.retry,
  };
  
  let lastError: Error | null = null;
  const maxAttempts = retryConfig.enabled ? (retryConfig.maxRetries || 0) + 1 : 1;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      if (API_CONFIG.ENABLE_LOG) {
        console.log(`[API] ${config.method?.toUpperCase() || 'GET'} ${url} (尝试 ${attempt}/${maxAttempts})`);
      }

      // 合并默认配置
      const mergedConfig: RequestConfig = {
        ...DEFAULT_CONFIG,
        ...config,
        headers: {
          ...DEFAULT_CONFIG.headers,
          ...config.headers,
        },
      };

      // 处理查询参数
      let fullUrl = config.baseURL ? `${config.baseURL}${url}` : url;
      if (config.params) {
        const queryString = buildQueryString(config.params);
        if (queryString) {
          fullUrl += (fullUrl.includes('?') ? '&' : '?') + queryString;
        }
      }

      // 发送请求
      const response = await fetchWithTimeout(fullUrl, mergedConfig);

      if (API_CONFIG.ENABLE_LOG) {
        console.log(`[API] 响应状态: ${response.status} ${fullUrl}`);
      }

      // 解析响应数据
      const contentType = response.headers.get('content-type');
      let responseData: unknown;

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      // 检查响应状态
      if (!response.ok) {
        // 尝试从响应体中提取错误信息
        if (responseData && typeof responseData === 'object') {
          const errorResponse = responseData as { message?: string; code?: number };
          const errorMessage = errorResponse.message || `HTTP Error: ${response.status} ${response.statusText}`;
          throw new Error(errorMessage);
        }
        throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
      }

      // 如果响应数据符合常见的包裹结构，直接返回 data 字段
      if (responseData && typeof responseData === 'object' && 'data' in (responseData as Record<string, unknown>)) {
        const r = responseData as { data: T; code?: number; message?: string; success?: boolean };
        // 后端可能使用 success 或 code 表示状态：
        // - success === false -> 失败
        // - code: 0 或 200 -> 成功，其他 -> 失败（仅当提供了 code 字段时）
        if (typeof r.success === 'boolean' && r.success === false) {
          throw new Error(r.message || '业务处理失败');
        }
        if (typeof r.code === 'number' && ![0, 200].includes(r.code)) {
          throw new Error(r.message || '业务处理失败');
        }
        
        // 调用成功回调
        if (config.onSuccess) {
          config.onSuccess(r.data);
        }
        
        return r.data;
      }

      // 否则直接返回响应数据（由调用方提供的泛型约束）
      const result = responseData as T;
      
      // 调用成功回调
      if (config.onSuccess) {
        config.onSuccess(result);
      }
      
      return result;

    } catch (error: unknown) {
      lastError = error instanceof Error ? error : new Error('未知错误');
      
      if (API_CONFIG.ENABLE_LOG) {
        console.error(`[API] 请求失败 (尝试 ${attempt}/${maxAttempts}):`, lastError.message);
      }
      
      // 判断是否需要重试
      const shouldRetry = 
        retryConfig.enabled && 
        attempt < maxAttempts && 
        isRetryableError(lastError);
      
      if (shouldRetry) {
        // 等待后重试
        await delay(retryConfig.retryDelay || 1000);
        continue;
      }
      
      // 不重试或已达到最大重试次数，抛出错误
      break;
    }
  }
  
  // 调用失败回调
  if (config.onError && lastError) {
    config.onError(lastError);
  }
  
  throw lastError || new Error('请求失败');
}

/**
 * GET 请求
 */
export function get<T = unknown>(url: string, config?: RequestConfig): Promise<T> {
  return apiRequest<T>(url, { ...config, method: 'GET' });
}

/**
 * POST 请求
 */
export function post<T = unknown>(
  url: string, 
  data?: unknown, 
  config?: RequestConfig
): Promise<T> {
  return apiRequest<T>(url, {
    ...config,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PUT 请求
 */
export function put<T = unknown>(
  url: string, 
  data?: unknown, 
  config?: RequestConfig
): Promise<T> {
  return apiRequest<T>(url, {
    ...config,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * DELETE 请求
 */
export function del<T = unknown>(url: string, config?: RequestConfig): Promise<T> {
  return apiRequest<T>(url, { ...config, method: 'DELETE' });
}

/**
 * PATCH 请求
 */
export function patch<T = unknown>(
  url: string, 
  data?: unknown, 
  config?: RequestConfig
): Promise<T> {
  return apiRequest<T>(url, {
    ...config,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
}

// 默认导出
export default {
  request: apiRequest,
  get,
  post,
  put,
  delete: del,
  patch,
};
